import { useQuery, useMutation } from "@/hooks/api";
import { queryKeys } from "@/constants/queryKeyFactory";
import { IProject } from "@/interfaces/project.interface";
import { PaginationParams, QueryOptions } from "@/interfaces/api.interface";
import { projectsSvc } from "@/services/project.service";
import { queryClient } from "@/lib/queryClient";
import { ApiResponse } from "@/interfaces/api.interface";

export function useProjects(params = {}, options: QueryOptions = {}) {
  return useQuery<ApiResponse<IProject[]>>(
    queryKeys.projects.list(params),
    () => projectsSvc.get<IProject>(params),
    options
  );
}

export function useCreateProject(options: QueryOptions = {}) {
  return useMutation<ApiResponse<IProject>, IProject>(
    (data: IProject) => projectsSvc.create<IProject>(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.root,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.userProjects.root,
        });
      },
    }
  );
}

export function useUpdateProject(options: QueryOptions = {}) {
  return useMutation<ApiResponse<IProject>, { id: string; data: IProject }>(
    ({ id, data }) => projectsSvc.update<IProject>(id, data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.root,
          refetchType: "active",
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.userProjects.root,
          refetchType: "active",
        });
      },
    }
  );
}

export function useAllProjects(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IProject[]>>(
    queryKeys.projects.all({ ...params }),
    () => projectsSvc.getAll<IProject>(params),
    options
  );
}

export function useUserProjects(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IProject[]>>(
    queryKeys.userProjects.all({ ...params }),
    () => projectsSvc.getUsersProjects(),
    options
  );
}
